import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Upload, Video, Eye, Heart, Settings, 
  Plus, Trash2, Lock, Loader2, BarChart3,
  User, Link as LinkIcon, Save, Radio, ExternalLink,
  Copy, Play, StopCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('videos');
  const [profile, setProfile] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);

  const isCreator = user?.subscription_tier === 'creator' || user?.subscription_tier === 'model';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isCreator) {
      fetchData();
    }
  }, [isAuthenticated, isCreator]);

  const fetchData = async () => {
    try {
      const [profileRes, videosRes] = await Promise.all([
        axios.get(`${API}/creator/my-profile`, getAuthHeader()),
        axios.get(`${API}/creator/my-videos`, getAuthHeader())
      ]);
      setProfile(profileRes.data.profile);
      setVideos(videosRes.data.videos || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!confirm('Are you sure you want to delete this video?')) return;
    
    try {
      await axios.delete(`${API}/creator/video/${videoId}`, getAuthHeader());
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success('Video deleted');
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  // Premium gate
  if (!isCreator) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="creator-locked">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gray-500" />
          </div>
          <h2 
            className="text-3xl font-normal tracking-wide uppercase mb-4"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Creator Dashboard
          </h2>
          <p className="text-gray-400 mb-6">
            Upload videos, build your audience, and monetize your content with a Creator or Model subscription.
          </p>
          <Button 
            onClick={() => navigate('/subscription')}
            className="bg-white text-black hover:bg-gray-200"
            data-testid="upgrade-btn"
          >
            Upgrade to Creator
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8" data-testid="creator-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-4xl font-normal tracking-wide uppercase"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Creator Studio
            </h1>
            <p className="text-gray-400 mt-1">Manage your content and profile</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setShowGoLiveModal(true)}
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-500/10"
              data-testid="go-live-btn"
            >
              <Radio className="w-4 h-4 mr-2" />
              Go Live
            </Button>
            <Button 
              onClick={() => setShowUploadModal(true)}
              className="bg-white text-black hover:bg-gray-200"
              data-testid="upload-video-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Video
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Videos', value: videos.length, icon: Video },
            { label: 'Total Views', value: profile?.total_views || 0, icon: Eye },
            { label: 'Total Likes', value: profile?.total_likes || 0, icon: Heart },
            { label: 'Followers', value: profile?.follower_count || 0, icon: User },
          ].map((stat, i) => (
            <div key={i} className="bg-[#121212] border border-white/10 rounded-lg p-4">
              <stat.icon className="w-5 h-5 text-gray-500 mb-2" />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 border-b border-white/10 mb-6 overflow-x-auto">
          {[
            { id: 'videos', label: 'My Videos', icon: Video },
            { id: 'streams', label: 'Live Streams', icon: Radio },
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-white text-white' 
                  : 'border-transparent text-gray-500 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : activeTab === 'videos' ? (
          <VideosTab videos={videos} onDelete={handleDeleteVideo} />
        ) : activeTab === 'streams' ? (
          <StreamsTab getAuthHeader={getAuthHeader} userId={user?.id} />
        ) : activeTab === 'profile' ? (
          <ProfileTab profile={profile} onUpdate={fetchData} getAuthHeader={getAuthHeader} />
        ) : (
          <AnalyticsTab profile={profile} videos={videos} />
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <UploadModal 
            onClose={() => setShowUploadModal(false)} 
            onSuccess={() => {
              setShowUploadModal(false);
              fetchData();
            }}
            getAuthHeader={getAuthHeader}
          />
        )}

        {/* Go Live Modal */}
        {showGoLiveModal && (
          <GoLiveModal 
            onClose={() => setShowGoLiveModal(false)} 
            getAuthHeader={getAuthHeader}
            userId={user?.id}
          />
        )}
      </div>
    </div>
  );
};

const VideosTab = ({ videos, onDelete }) => {
  if (videos.length === 0) {
    return (
      <div className="text-center py-12">
        <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No videos uploaded yet</p>
        <p className="text-gray-600 text-sm">Click "Upload Video" to get started</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {videos.map(video => (
        <div key={video.id} className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden group">
          <div className="aspect-video relative">
            <img 
              src={video.thumbnail} 
              alt={video.title}
              className="w-full h-full object-cover"
            />
            <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 text-white text-xs rounded">
              {video.duration}
            </span>
            {video.is_premium && (
              <span className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500 text-black text-xs font-medium rounded">
                Premium
              </span>
            )}
          </div>
          <div className="p-4">
            <h3 className="text-white font-medium line-clamp-2 mb-2">{video.title}</h3>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" /> {video.views}
                </span>
                <span className="flex items-center gap-1">
                  <Heart className="w-3 h-3" /> {video.likes}
                </span>
              </div>
              <button 
                onClick={() => onDelete(video.id)}
                className="p-2 hover:bg-red-500/20 rounded text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProfileTab = ({ profile, onUpdate, getAuthHeader }) => {
  const [formData, setFormData] = useState({
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
    social_links: profile?.social_links || {}
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/creator/profile`, formData, getAuthHeader());
      toast.success('Profile updated');
      onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="space-y-6">
        <div>
          <label className="block text-sm text-gray-400 mb-2">Display Name</label>
          <Input
            value={formData.display_name}
            onChange={(e) => setFormData({...formData, display_name: e.target.value})}
            className="bg-[#1A1A1A] border-white/10"
            data-testid="profile-display-name"
          />
        </div>
        
        <div>
          <label className="block text-sm text-gray-400 mb-2">Bio</label>
          <Textarea
            value={formData.bio}
            onChange={(e) => setFormData({...formData, bio: e.target.value})}
            className="bg-[#1A1A1A] border-white/10 min-h-[120px]"
            placeholder="Tell viewers about yourself..."
            data-testid="profile-bio"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-400 mb-2">Social Links</label>
          <div className="space-y-3">
            {['twitter', 'instagram', 'onlyfans'].map(platform => (
              <div key={platform} className="flex items-center gap-3">
                <span className="w-24 text-gray-500 capitalize text-sm">{platform}</span>
                <Input
                  value={formData.social_links[platform] || ''}
                  onChange={(e) => setFormData({
                    ...formData, 
                    social_links: {...formData.social_links, [platform]: e.target.value}
                  })}
                  className="bg-[#1A1A1A] border-white/10"
                  placeholder={`${platform}.com/...`}
                />
              </div>
            ))}
          </div>
        </div>

        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-white text-black hover:bg-gray-200"
          data-testid="save-profile-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const AnalyticsTab = ({ profile, videos }) => {
  return (
    <div className="text-center py-12">
      <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
      <h3 className="text-xl text-white mb-2">Analytics Coming Soon</h3>
      <p className="text-gray-400">
        Detailed insights about your content performance will be available here.
      </p>
    </div>
  );
};

const UploadModal = ({ onClose, onSuccess, getAuthHeader }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    duration: '',
    tags: '',
    is_premium: false
  });
  const [uploading, setUploading] = useState(false);
  const [detectedPlatform, setDetectedPlatform] = useState(null);

  const supportedPlatforms = [
    { name: 'YouTube', domain: 'youtube.com', icon: '📺' },
    { name: 'YouTube', domain: 'youtu.be', icon: '📺' },
    { name: 'Vimeo', domain: 'vimeo.com', icon: '🎬' },
    { name: 'Dailymotion', domain: 'dailymotion.com', icon: '📹' },
    { name: 'Streamable', domain: 'streamable.com', icon: '🎥' },
    { name: 'Direct Link', domain: '.mp4', icon: '🔗' },
    { name: 'Direct Link', domain: '.webm', icon: '🔗' },
  ];

  const detectPlatform = (url) => {
    if (!url) return null;
    const platform = supportedPlatforms.find(p => url.toLowerCase().includes(p.domain));
    return platform || null;
  };

  const handleUrlChange = (url) => {
    setFormData({...formData, video_url: url});
    setDetectedPlatform(detectPlatform(url));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.video_url || !formData.duration) {
      toast.error('Please fill in required fields');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/creator/upload-video`, {
        title: formData.title,
        description: formData.description,
        video_url: formData.video_url,
        thumbnail_base64: formData.thumbnail_url || null,
        duration: formData.duration,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        is_premium: formData.is_premium
      }, getAuthHeader());
      toast.success('Video uploaded successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="upload-modal">
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg max-w-lg w-full p-6 my-8">
        <h2 
          className="text-2xl font-normal tracking-wide uppercase mb-2"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }}
        >
          Upload Video
        </h2>
        <p className="text-gray-500 text-sm mb-6">Share videos from YouTube, Vimeo, or direct links</p>

        {/* Supported platforms */}
        <div className="bg-[#121212] rounded-lg p-4 mb-6">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Supported Platforms</p>
          <div className="flex flex-wrap gap-2">
            {['YouTube', 'Vimeo', 'Dailymotion', 'Streamable', 'Direct MP4/WebM'].map(platform => (
              <span key={platform} className="px-3 py-1 bg-white/5 rounded-full text-xs text-gray-400">
                {platform}
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="bg-[#1A1A1A] border-white/10"
              placeholder="Give your video a catchy title"
              required
              data-testid="video-title-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Video URL *</label>
            <Input
              value={formData.video_url}
              onChange={(e) => handleUrlChange(e.target.value)}
              className="bg-[#1A1A1A] border-white/10"
              placeholder="https://youtube.com/watch?v=... or direct link"
              required
              data-testid="video-url-input"
            />
            {detectedPlatform && (
              <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                ✓ Detected: {detectedPlatform.icon} {detectedPlatform.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Thumbnail URL (optional)</label>
            <Input
              value={formData.thumbnail_url}
              onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})}
              className="bg-[#1A1A1A] border-white/10"
              placeholder="https://... (leave empty to auto-generate)"
              data-testid="video-thumbnail-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Duration * (e.g., 10:30)</label>
            <Input
              value={formData.duration}
              onChange={(e) => setFormData({...formData, duration: e.target.value})}
              className="bg-[#1A1A1A] border-white/10"
              placeholder="MM:SS"
              required
              data-testid="video-duration-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="bg-[#1A1A1A] border-white/10 min-h-[80px]"
              placeholder="Describe your video..."
              data-testid="video-description-input"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Tags (comma separated)</label>
            <Input
              value={formData.tags}
              onChange={(e) => setFormData({...formData, tags: e.target.value})}
              className="bg-[#1A1A1A] border-white/10"
              placeholder="amateur, solo, hd"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <input
              type="checkbox"
              checked={formData.is_premium}
              onChange={(e) => setFormData({...formData, is_premium: e.target.checked})}
              className="w-4 h-4 accent-amber-500"
            />
            <div>
              <span className="text-amber-400 font-medium">Premium content</span>
              <p className="text-gray-500 text-xs">Only subscribers can view this video</p>
            </div>
          </label>

          <div className="flex gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="flex-1 border-white/20"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={uploading}
              className="flex-1 bg-white text-black hover:bg-gray-200"
              data-testid="submit-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StreamsTab = ({ getAuthHeader, userId }) => {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyStreams();
  }, []);

  const fetchMyStreams = async () => {
    try {
      const response = await axios.get(`${API}/streams/my-streams`, getAuthHeader());
      setStreams(response.data.streams || []);
    } catch (error) {
      console.error('Error fetching streams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEndStream = async (streamId) => {
    try {
      await axios.post(`${API}/streams/${streamId}/end`, {}, getAuthHeader());
      toast.success('Stream ended');
      fetchMyStreams();
    } catch (error) {
      toast.error('Failed to end stream');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (streams.length === 0) {
    return (
      <div className="text-center py-12">
        <Radio className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No streams yet</p>
        <p className="text-gray-600 text-sm">Click "Go Live" to start streaming</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {streams.map(stream => (
        <div key={stream.id} className="bg-[#121212] border border-white/10 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-3 h-3 rounded-full ${stream.status === 'live' ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`} />
            <div>
              <h3 className="text-white font-medium">{stream.title}</h3>
              <p className="text-gray-500 text-sm">
                {stream.status === 'live' ? 'Currently Live' : `Ended ${new Date(stream.ended_at).toLocaleDateString()}`}
              </p>
            </div>
          </div>
          {stream.status === 'live' && (
            <Button 
              onClick={() => handleEndStream(stream.id)}
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-500/10"
            >
              <StopCircle className="w-4 h-4 mr-2" />
              End Stream
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const GoLiveModal = ({ onClose, getAuthHeader, userId }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    stream_url: '',
    platform: 'youtube',
    is_premium: false
  });
  const [starting, setStarting] = useState(false);

  const platforms = [
    { id: 'youtube', name: 'YouTube Live', icon: '📺', instructions: 'Stream via YouTube Studio, then paste your live video URL here' },
    { id: 'twitch', name: 'Twitch', icon: '🎮', instructions: 'Stream to Twitch, then paste your channel URL here' },
    { id: 'custom', name: 'Custom RTMP', icon: '🔗', instructions: 'Paste any embed-compatible stream URL' },
  ];

  const handleStartStream = async () => {
    if (!formData.title || !formData.stream_url) {
      toast.error('Please fill in required fields');
      return;
    }

    setStarting(true);
    try {
      const response = await axios.post(`${API}/streams/start`, {
        title: formData.title,
        description: formData.description,
        stream_url: formData.stream_url,
        platform: formData.platform,
        is_premium: formData.is_premium
      }, getAuthHeader());
      
      toast.success('Stream started! Viewers can now watch.');
      onClose();
      navigate(`/live/${response.data.stream_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start stream');
    } finally {
      setStarting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="go-live-modal">
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg max-w-2xl w-full p-6 my-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <Radio className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h2 
              className="text-2xl font-normal tracking-wide uppercase"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Go Live
            </h2>
            <p className="text-gray-500 text-sm">Start streaming to your audience</p>
          </div>
        </div>

        {step === 1 ? (
          <>
            {/* Step 1: Setup Instructions */}
            <div className="bg-[#121212] rounded-lg p-6 mb-6">
              <h3 className="text-white font-medium mb-4">How to Go Live</h3>
              <ol className="space-y-4 text-gray-400 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Start streaming on YouTube, Twitch, or your preferred platform using OBS or similar software</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Copy your live stream URL (e.g., youtube.com/watch?v=XXXXX)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Paste it here and your stream will be embedded on VelvetStream</span>
                </li>
              </ol>
            </div>

            {/* OBS Settings Helper */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
              <h4 className="text-blue-400 font-medium mb-2">Need OBS?</h4>
              <p className="text-gray-400 text-sm mb-3">Download OBS Studio (free) to stream to YouTube or Twitch</p>
              <a 
                href="https://obsproject.com/download" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-400 text-sm hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Download OBS Studio
              </a>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={onClose}
                className="flex-1 border-white/20"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setStep(2)}
                className="flex-1 bg-white text-black hover:bg-gray-200"
              >
                Continue
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Step 2: Stream Details */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Stream Platform</label>
                <div className="grid grid-cols-3 gap-3">
                  {platforms.map(platform => (
                    <button
                      key={platform.id}
                      onClick={() => setFormData({...formData, platform: platform.id})}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.platform === platform.id 
                          ? 'border-white bg-white/10' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <span className="text-2xl block mb-1">{platform.icon}</span>
                      <span className="text-white text-sm">{platform.name}</span>
                    </button>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  {platforms.find(p => p.id === formData.platform)?.instructions}
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Stream Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="bg-[#1A1A1A] border-white/10"
                  placeholder="What are you streaming today?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Stream URL *</label>
                <Input
                  value={formData.stream_url}
                  onChange={(e) => setFormData({...formData, stream_url: e.target.value})}
                  className="bg-[#1A1A1A] border-white/10"
                  placeholder="https://youtube.com/watch?v=... or twitch.tv/..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-[#1A1A1A] border-white/10 min-h-[80px]"
                  placeholder="Tell viewers what to expect..."
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <input
                  type="checkbox"
                  checked={formData.is_premium}
                  onChange={(e) => setFormData({...formData, is_premium: e.target.checked})}
                  className="w-4 h-4 accent-amber-500"
                />
                <div>
                  <span className="text-amber-400 font-medium">Premium stream</span>
                  <p className="text-gray-500 text-xs">Only subscribers can watch</p>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setStep(1)}
                className="border-white/20"
              >
                Back
              </Button>
              <Button 
                onClick={handleStartStream}
                disabled={starting}
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Radio className="w-4 h-4 mr-2" />}
                Start Stream
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreatorDashboard;
